import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import { Button } from '@rekrytering/ui/components/button'
import { Input } from '@rekrytering/ui/components/input'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

export function BrandingSettingsPage() {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [primaryColor, setPrimaryColor] = useState('')
  const [website, setWebsite] = useState('')
  const [tagline, setTagline] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const { data: branding } = useQuery(
    convexQuery(api.features.org.org_branding.getMyOrgBranding, {}),
  )

  const { mutateAsync: generateUploadUrl } = useMutation({
    mutationFn: useConvexMutation(
      api.features.org.org_branding.generateMyLogoUploadUrl,
    ),
  })

  const { mutateAsync: saveMyOrgLogo } = useMutation({
    mutationFn: useConvexMutation(api.features.org.org_branding.saveMyOrgLogo),
  })

  const { mutateAsync: updateMyOrgBranding } = useMutation({
    mutationFn: useConvexMutation(
      api.features.org.org_branding.updateMyOrgBranding,
    ),
  })

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setLogoPreviewUrl(URL.createObjectURL(file))
    setUploading(true)

    try {
      const uploadUrl = await generateUploadUrl({})
      const res = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': file.type },
        body: file,
      })
      const { storageId } = await res.json()
      await saveMyOrgLogo({ storageId })
    } finally {
      setUploading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await updateMyOrgBranding({
        primaryColor: primaryColor || undefined,
        website: website || undefined,
        tagline: tagline || undefined,
      })
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex h-full w-full flex-col gap-6">
      <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
        <div className="flex w-full flex-col gap-6 rounded-lg p-6">
          <div className="flex flex-col gap-2">
            <h2 className="text-xl font-medium text-primary">
              {t('settings.branding.title')}
            </h2>
            <p className="text-sm font-normal text-primary/60">
              {t('settings.branding.description')}
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium text-primary">
              {t('settings.branding.logoTitle')}
            </p>
            <p className="text-sm font-normal text-primary/60">
              {t('settings.branding.logoDescription')}
            </p>
            <div className="flex items-center gap-4">
              {logoPreviewUrl && (
                <img
                  src={logoPreviewUrl}
                  alt="Logo preview"
                  className="h-12 w-12 rounded-md object-contain"
                />
              )}
              <Button
                size="sm"
                variant="outline"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploading
                  ? t('settings.branding.uploading')
                  : t('settings.branding.uploadLogo')}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleLogoChange}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium text-primary">
              {t('settings.branding.primaryColorTitle')}
            </p>
            <p className="text-sm font-normal text-primary/60">
              {t('settings.branding.primaryColorDescription')}
            </p>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={primaryColor || branding?.primaryColor || '#000000'}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="h-9 w-16 cursor-pointer rounded-md border border-border bg-transparent"
              />
              <Input
                placeholder="#000000"
                value={primaryColor || branding?.primaryColor || ''}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="w-36 bg-transparent"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium text-primary">
              {t('settings.branding.websiteTitle')}
            </p>
            <p className="text-sm font-normal text-primary/60">
              {t('settings.branding.websiteDescription')}
            </p>
            <Input
              type="url"
              placeholder="https://acme.com"
              value={website || branding?.website || ''}
              onChange={(e) => setWebsite(e.target.value)}
              className="w-80 bg-transparent"
            />
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium text-primary">
              {t('settings.branding.taglineTitle')}
            </p>
            <p className="text-sm font-normal text-primary/60">
              {t('settings.branding.taglineDescription')}
            </p>
            <Input
              placeholder={t('settings.branding.taglinePlaceholder')}
              value={tagline || branding?.tagline || ''}
              onChange={(e) => setTagline(e.target.value)}
              className="w-80 bg-transparent"
            />
          </div>
        </div>

        <div className="flex min-h-14 w-full items-center justify-between rounded-lg rounded-t-none border-t border-border bg-secondary px-6 dark:bg-card">
          <p className="text-sm font-normal text-primary/60">
            {t('settings.branding.hint')}
          </p>
          <Button size="sm" disabled={saving} onClick={handleSave}>
            {saved
              ? t('settings.branding.saved')
              : saving
                ? t('settings.branding.saving')
                : t('settings.branding.save')}
          </Button>
        </div>
      </div>
    </div>
  )
}
